import type { DailyThresholdRow } from '@/types/waterfall'

export const Y_AXIS_MIN = 0
export const Y_AXIS_STEP = 500_000
export const CHART_MAX_POINTS = 400

export function yMaxForBalances(balances: number[]): number {
  const maxBal = balances.length === 0 ? 0 : Math.max(0, ...balances)
  if (maxBal === 0) {
    return Y_AXIS_STEP
  }
  return Math.ceil(maxBal / Y_AXIS_STEP) * Y_AXIS_STEP
}

export function balanceToY(
  balance: number,
  innerH: number,
  padTop: number,
  yMax: number,
): number {
  const clamped = Math.min(Math.max(balance, Y_AXIS_MIN), yMax)
  const frac = (clamped - Y_AXIS_MIN) / (yMax - Y_AXIS_MIN)
  return padTop + innerH - frac * innerH
}

export function dateToX(
  dateIso: string,
  xMinMs: number,
  xMaxMs: number,
  padLeft: number,
  innerW: number,
): number {
  const ms = Date.parse(dateIso)
  const span = xMaxMs - xMinMs
  const frac = span <= 0 ? 0.5 : (ms - xMinMs) / span
  return padLeft + frac * innerW
}

export function maxIsoDate(...dates: (string | null | undefined)[]): string {
  const valid = dates.filter((d): d is string => Boolean(d))
  if (valid.length === 0) {
    return ''
  }
  return valid.reduce((a, b) => (a > b ? a : b))
}

export function chartXBounds(params: {
  startDate: string
  endDate: string
  actualRows: DailyThresholdRow[]
  projectedRows: DailyThresholdRow[]
}): { minDate: string; maxDate: string; minMs: number; maxMs: number } {
  const lastActual = params.actualRows.at(-1)?.date
  const lastProjected = params.projectedRows.at(-1)?.date
  const minDate = params.startDate
  const maxDate = maxIsoDate(params.endDate, lastActual, lastProjected)
  const minMs = Date.parse(minDate)
  const maxMs = Date.parse(maxDate)
  return { minDate, maxDate, minMs, maxMs }
}

export function splitActualForChart(
  rows: DailyThresholdRow[],
  spliceDate: string | null,
  todayIso: string,
): DailyThresholdRow[] {
  const cutoff = spliceDate ?? todayIso
  return rows.filter((row) => row.date <= cutoff)
}

export function lastActualAtSplice(
  rows: DailyThresholdRow[],
  spliceDate: string,
): DailyThresholdRow | null {
  const atOrBefore = rows.filter((row) => row.date <= spliceDate)
  if (atOrBefore.length === 0) {
    return null
  }
  return atOrBefore[atOrBefore.length - 1]!
}

export function buildOverlayPoints(
  allActualRows: DailyThresholdRow[],
  projectedRows: DailyThresholdRow[],
  spliceDate: string | null,
): DailyThresholdRow[] {
  if (!spliceDate || projectedRows.length === 0) {
    return []
  }
  const last = lastActualAtSplice(allActualRows, spliceDate)
  if (!last) {
    return []
  }
  const join: DailyThresholdRow = { ...last, date: spliceDate }
  return [join, ...projectedRows]
}

export function downsampleThresholdRows(
  rows: DailyThresholdRow[],
  maxPoints = CHART_MAX_POINTS,
  pinDates: string[] = [],
): DailyThresholdRow[] {
  if (rows.length <= maxPoints) {
    return rows
  }
  const pinSet = new Set(pinDates)
  const step = Math.ceil(rows.length / maxPoints)
  return rows.filter(
    (row, i) => i % step === 0 || i === rows.length - 1 || pinSet.has(row.date),
  )
}

/** Vertices for a dashed overlay: join + month-ends + last. Daily points make dashes unreadable. */
export function monthEndChartRows(rows: DailyThresholdRow[]): DailyThresholdRow[] {
  if (rows.length <= 2) {
    return rows
  }
  const out: DailyThresholdRow[] = []
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i]!
    const isFirst = i === 0
    const isLast = i === rows.length - 1
    const month = row.date.slice(0, 7)
    const nextMonth = rows[i + 1]?.date.slice(0, 7)
    const monthEnd = isLast || nextMonth !== month
    if (isFirst || isLast || monthEnd) {
      if (out.at(-1)?.date !== row.date) {
        out.push(row)
      }
    }
  }
  return out
}

export function computeXLabelDates(minMs: number, maxMs: number, maxLabels = 6): string[] {
  if (!Number.isFinite(minMs) || !Number.isFinite(maxMs)) {
    return []
  }
  if (maxMs <= minMs) {
    return [new Date(minMs).toISOString().slice(0, 10)]
  }
  const labelCount = Math.max(2, maxLabels)
  const dates: string[] = []
  for (let i = 0; i < labelCount; i++) {
    const frac = i / (labelCount - 1)
    const ms = minMs + frac * (maxMs - minMs)
    dates.push(new Date(ms).toISOString().slice(0, 10))
  }
  return dates
}

export type ChartHoverPoint = {
  row: DailyThresholdRow
  projected: boolean
  x: number
  y: number
}
